import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR, fmtDate } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Megaphone, Loader2, RefreshCw, Wallet } from "lucide-react";

const AD_TYPE_LABELS = {
  hoarding: "Hoarding", led_display: "LED Display", banner: "Banner",
  wall_wrap: "Wall Wrap", kiosk: "Kiosk", bus_shelter: "Bus Shelter",
};

export default function AdDetail() {
  const { aid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const officer = user && ["data_entry_operator", "revenue_officer", "executive_officer", "super_admin", "municipal_commissioner", "state_admin"].includes(user.role);

  const load = () => api.get(`/advertisements/${aid}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [aid]);

  const renew = async () => {
    setRenewing(true);
    try {
      const r = await api.post("/advertisements/renew", { advertisement_id: data.advertisement.id, duration_months: 12 });
      toast.success(`Renewal demand created: ${formatINR(r.data.fee)}`);
      load();
    } catch (err) {
      toast.error("Renewal failed", { description: err.response?.data?.detail });
    } finally { setRenewing(false); }
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const a = data.advertisement;
  const demands = data.demands || [];

  return (
    <div className="space-y-6" data-testid="ad-detail-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Advertisement</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[#E8702A]" /> {a.business_name}
          </h1>
          <div className="text-xs font-mono text-slate-500 mt-1">{a.ad_code}</div>
        </div>
        <div className="flex gap-2">
          {officer && (
            <Button onClick={renew} disabled={renewing} className="bg-[#E8702A] hover:bg-[#d95f1c] text-white" data-testid="btn-renew-ad">
              <RefreshCw className="w-4 h-4 mr-1" /> {renewing ? "Renewing..." : "Renew (+12m)"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardContent className="p-6 grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-slate-500">Advertiser</div><div>{a.advertiser_name}</div></div>
          <div><div className="text-xs text-slate-500">Contact</div><div>{a.advertiser_phone || "—"}</div></div>
          <div className="col-span-2"><div className="text-xs text-slate-500">Location</div><div>{a.location_description}</div></div>
          <div><div className="text-xs text-slate-500">Ward / Zone</div><div>{a.ward} · {a.zone || "—"}</div></div>
          <div><div className="text-xs text-slate-500">Type</div><Badge variant="outline">{AD_TYPE_LABELS[a.ad_type] || a.ad_type}</Badge></div>
          <div><div className="text-xs text-slate-500">Size</div><div>{a.width_ft} × {a.height_ft} ft <span className="text-slate-400">({a.sqft || (a.width_ft * a.height_ft)} sqft)</span></div></div>
          <div><div className="text-xs text-slate-500">Duration</div><div>{a.duration_months} months</div></div>
          <div><div className="text-xs text-slate-500">Start Date</div><div>{fmtDate(a.start_date)}</div></div>
          <div><div className="text-xs text-slate-500">End Date</div><div>{fmtDate(a.end_date)}</div></div>
        </CardContent></Card>

        <Card className="bg-[#0F2942] text-white"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest opacity-70">Annual Fee</div>
          <div className="text-3xl font-heading font-bold mt-2">{formatINR(a.fee_amount)}</div>
          <div className="mt-4 text-xs opacity-80">Status</div>
          <Badge className="mt-1 capitalize" variant="secondary">{a.status}</Badge>
          {a.total_dues > 0 && (
            <>
              <div className="text-xs opacity-80 mt-4">Outstanding</div>
              <div className="text-xl font-heading font-bold text-[#F5A623]">{formatINR(a.total_dues)}</div>
            </>
          )}
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 text-sm font-semibold">Demand History</div>
          <table className="w-full text-sm" data-testid="ad-demands-table">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-2 px-4">Demand ID</th>
                <th className="text-left py-2 px-4">Period</th>
                <th className="text-right py-2 px-4">Amount</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="text-left py-2 px-4">Due Date</th>
                <th className="text-right py-2 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {demands.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No demands yet.</td></tr>}
              {demands.map((d) => (
                <tr key={d.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="py-2 px-4 font-mono text-xs">{d.demand_id}</td>
                  <td className="py-2 px-4">{d.period}</td>
                  <td className="py-2 px-4 text-right font-mono">{formatINR(d.total)}</td>
                  <td className="py-2 px-4"><Badge variant={d.status === "paid" ? "default" : d.status === "pending" ? "outline" : "destructive"} className="capitalize">{d.status}</Badge></td>
                  <td className="py-2 px-4">{fmtDate(d.due_date)}</td>
                  <td className="py-2 px-4 text-right">
                    {d.status !== "paid" && <Link to={`/payments?demand=${d.id}`}><Button size="sm" variant="outline" className="gap-1"><Wallet className="w-3.5 h-3.5" /> Pay</Button></Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
